import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { IconEdit, IconEye } from '@tabler/icons-react';
import docsMutation from '@/api/mutitations/docs';
import prepareTranslate from '@/helpers/dictionary';
import { useDisclosure } from '@mantine/hooks';
import Table from '../Table';
import { extractFilterOptions, filterAndSortData } from '../Table/Table.functions';
import ViewAdditionalFee from './ViewAdditionalFee';
import EditAdditionalFee from '../EditAdditionalFee';

const ViewAgentAdditionalFees = (props) => {
  const {
    agent,
    id,
    setKey,
    handleAgentUpdateClick,
  } = props;

  const [additionalFees, setAdditionalFees] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedFee, setSelectedFee] = useState(null);

  const [viewOpened, { open: openView, close: closeView }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();
  const t = prepareTranslate();

  useEffect(() => {
    const filtered = agent?.additionalFees?.filter((e) => e.status !== 'archive') || [];
    setAdditionalFees(filtered);
  }, [agent]);

  const globalColumnsProps = {
    isVisible: true,
    isFilterActive: true,
    sortable: true,
  };

  const columns = [
    {
      title: 'שם חיוב',
      accessor: 'type',
      ...globalColumnsProps,
    },
    {
      title: 'סכום',
      accessor: 'value',
      ...globalColumnsProps,
    },
    {
      title: 'תאריך',
      accessor: 'date',
      transforms: [{ func: 'date', params: { withTime: false } }],
      filterType: 'datePicker',
      ...globalColumnsProps,
    },
    {
      title: 'הערות',
      accessor: 'comments',
      ...globalColumnsProps,
    },
    {
      title: 'האם שולם',
      accessor: 'isPaid',
      transforms: ['boolean'],
      ...globalColumnsProps,
    },
    {
      title: 'מתי שולם',
      accessor: 'payDate',
      transforms: [{ func: 'date', params: { withTime: false } }],
      filterType: 'datePicker',
      ...globalColumnsProps,
    },
    {
      title: 'פעולות',
      actions: [
        {
          title: 'צפייה',
          icon: <IconEye />,
          showLabel: true,
          isPrimary: true,
          action: (_, row) => {
            setSelectedFee(row);
            openView();
          },
        },
        {
          title: 'עריכה',
          icon: <IconEdit />,
          showLabel: true,
          isPrimary: true,
          action: (_, row) => {
            setSelectedFee(row);
            openEdit();
          },
        },
      ],
    },
  ];

  useEffect(() => {
    const options = extractFilterOptions(additionalFees, columns);
    setFilterOptions(options);

    const initialFilters = columns
      .filter((col) => col.accessor)
      .reduce((acc, col) => {
        acc[col.accessor] = '';
        return acc;
      }, {});
    setColumnFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalFees]);

  const getData = async (start, end, query, sort) => {
    const filteredAndSorted = filterAndSortData(additionalFees, query, sort);

    return {
      data: filteredAndSorted,
      count: filteredAndSorted.length,
    };
  };

  // eslint-disable-next-line consistent-return
  const createFile = async (option, query) => {
    const requestColumns = columns.filter((column) => column.title !== 'פעולות');
    const exportData = {
      query: { ...query, _id: id },
      fileType: option,
      service: 'agent',
      columns: requestColumns,
      titleArray: [''],
      furtherData: {
        select: 'additionalFees',
      },
    };

    const fileTitleBase = t('components.viewAgentAdditionalFees.fileTile', [agent.personalDetails.fullName]);
    const fileTitle = option === 'pdf' ? `${fileTitleBase}.pdf` : `${fileTitleBase}.xlsx`;

    try {
      const fileData = await exportFileMutate(exportData);
      return { fileData, fileTitle };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching additionalFees:', error);
    }
  };

  const handleUpdate = async (data) => {
    const updatedAdditionalFee = data.fee;
    // eslint-disable-next-line max-len
    const updatedAdditionalFees = agent?.additionalFees?.map((fee) => (fee._id === selectedFee._id ? updatedAdditionalFee : fee));
    setAdditionalFees(updatedAdditionalFees);
    return handleAgentUpdateClick({ additionalFees: updatedAdditionalFees });
  };

  return (
    <>
        <Table
          key={additionalFees.length}
          columns={columns}
          getData={getData}
          isColumnsActive={false}
          exportOptions={['excel']}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          filterOptions={filterOptions}
          createFile={createFile}
        />

      <ViewAdditionalFee
        open={openView}
        close={closeView}
        opened={viewOpened}
        fee={selectedFee}
      />

      <EditAdditionalFee
        open={openEdit}
        close={closeEdit}
        opened={editOpened}
        fee={selectedFee}
        setKey={setKey}
        id={id}
        handleAgentUpdateClick={handleUpdate}
      />
    </>
  );
};

ViewAgentAdditionalFees.propTypes = {
  agent: PropTypes.object,
  id: PropTypes.string,
  setKey: PropTypes.func,
  handleAgentUpdateClick: PropTypes.func,
};

export default ViewAgentAdditionalFees;
